import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import format from 'string-template';
import PropTypes from 'prop-types';

import InputLabel from '../../../../../../components/v2/InputLabel';
import ConfirmDialog from '../../../../../components/ConfirmDialog';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import useToast from '../../../../../hooks/useToast';
import CoachHistory, { HistoryEventType, getOnboardingChecklistByCoach } from '../../../../../Model/CoachHistory';
import LoggedInUserContext from '../../../../../../context/LoggedInUserContext';
import CoachOnboardingTask from '../../../../../components/CoachMissingSettingsModal/CoachOnboardingTask';
import OnboardingProgressBar from '../../OnboardingProgressBar';
import OnboardingCoachNotificationsContext from '../../../../../context/OnboardingCoachNotificationsContext';
import LaunchDateAlert from './LaunchDateAlert';
import { getPendingEvents, getOnboardingChecklistAnswers } from './utils';
import {
  StyledLoadingPage,
  Container,
  SubContainer,
  StyledCheckbox,
  ContentContainer,
  ContentText,
  SubtitleText,
  StyledProgressBox,
  ContentSubText,
} from './styles';
import texts from './texts';

const PendingList = ({
  onboardingDoc,
  history,
  isReady,
  onRefresh,
  isEnrolledInISProgram,
}) => {
  const {
    userDoc: {
      name: authUserName,
    },
    userId: authUserId,
  } = useContext(LoggedInUserContext);

  const [completionEvent, setCompletionEvent] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [localLaunchDate, setLocalLaunchDate] = useState(onboardingDoc.launchDate);
  const { onboardingDocument } = onboardingDoc;

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const { TOTAL_ONBOARDING_TASKS_EXISTING_COACH } = useContext(OnboardingCoachNotificationsContext);

  // Update local launch date when onboardingDoc changes
  useEffect(() => {
    setLocalLaunchDate(onboardingDoc.launchDate);
  }, [onboardingDoc.launchDate]);

  const {
    manualEvents,
    coachTasks,
    camTasks,
    contentURLSubTasks,
  } = useMemo(() => getPendingEvents(history, onboardingDoc.isNewCoach, isEnrolledInISProgram),
    [
      history,
      onboardingDoc.isNewCoach,
      isEnrolledInISProgram,
    ]);

  const onboardingChecklistAnswers = useMemo(() => getOnboardingChecklistAnswers(history), [history]);

  const onboardingEvent = history.find((event) => event.type === HistoryEventType.WHITE_LISTED);
  const accountCreatedEvent = history.find((event) => event.type === HistoryEventType.ACCOUNT_CREATED);

  // Calculate completed coach tasks
  const completedCoachTasks = onboardingDoc.isNewCoach
    ? getOnboardingChecklistByCoach(accountCreatedEvent?.createdAt).length - coachTasks.length
    : TOTAL_ONBOARDING_TASKS_EXISTING_COACH - coachTasks.length;

  const handleCheckItem = useCallback((event) => {
    // If the coach is not signed up, do not allow to complete manual events.
    if (!onboardingDoc.coach) {
      showToast(texts.notSignedUp, { error: true });
      return;
    }
    setCompletionEvent(event);
  }, [
    onboardingDoc.coach,
    showToast,
  ]);

  const handleConfirm = useCallback(async () => {
    setIsProcessing(true);
    if (completionEvent) {
      await CoachHistory.addCoachHistoryItem({
        type: completionEvent,
        coachId: onboardingDoc.coach,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
      });
    } else {
      showToast(texts.completionFailed, { error: true });
    }
    if (isComponentMountedRef.current) {
      setCompletionEvent(null);
      setIsProcessing(false);
    }
  }, [
    showToast,
    isComponentMountedRef,
    authUserId,
    authUserName,
    onboardingDoc.coach,
    completionEvent,
  ]);

  const handleLaunchDateChange = useCallback(async (newLaunchDate) => {
    setIsProcessing(true);
    try {
      await onboardingDocument.updateLaunchDate(newLaunchDate);
      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.LAUNCH_DATE_EDITED,
        coachId: onboardingDoc.coach,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
        metadata: {
          newLaunchDate,
          oldLaunchDate: localLaunchDate,
        },
      });
      setLocalLaunchDate(newLaunchDate);
      showToast(texts.launchDateUpdated);
      onRefresh();
    } catch (error) {
      showToast(format(texts.launchDateUpdateFailed, { error: error.message }), { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsProcessing(false);
    }
  }, [
    onboardingDocument,
    showToast,
    isComponentMountedRef,
    onRefresh,
    authUserId,
    authUserName,
    onboardingDoc.coach,
    localLaunchDate,
  ]);

  if (!isReady) {
    return <StyledLoadingPage />;
  }

  /* If the coach is inelligible for pending items or doesn't have any pending items, show a message.
   * We consider the coach inelligible if the onboarding event is not recorded
   */
  if (!onboardingEvent || (!manualEvents.length && !coachTasks.length && !camTasks.length
    && !contentURLSubTasks.length)) {
    return (
      <Container>
        {texts.noPending}
      </Container>
    );
  }

  return (
    <>
      <LaunchDateAlert launchDate={localLaunchDate} onLaunchDateChange={handleLaunchDateChange} />
      <Container>
        {!!manualEvents.length && (
        <SubContainer>
          <InputLabel>{texts.manualChecklist}</InputLabel>
          {manualEvents.map((event) => (
            <StyledCheckbox
              key={event}
              isChecked={false}
              onChange={() => handleCheckItem(event)}
              description={texts.eventText[event]}
            />
          ))}
        </SubContainer>
        )}
        <SubContainer>
          <InputLabel>{texts.automaticChecklist}</InputLabel>
          {accountCreatedEvent && (
            <>
              <SubtitleText>{texts.coachTasks}</SubtitleText>
              <StyledProgressBox>
                <OnboardingProgressBar
                  onboardingChecklistCount={completedCoachTasks}
                  isCoachLeadView
                  isNewCoachAccount={onboardingDoc.isNewCoach}
                  accountCreatedAt={accountCreatedEvent.createdAt}
                  isEnrolledInISProgram={isEnrolledInISProgram}
                />
              </StyledProgressBox>
              {!!coachTasks.length && (
              <ContentContainer>
                {coachTasks.map((event) => {
                  if (event === HistoryEventType.CONTENT_URLS_SUBMITTED) {
                    return (
                      <div key={event}>
                        <ContentText>{`- ${texts.eventText[event]}`}</ContentText>
                        {contentURLSubTasks.map((subTask) => (
                          <ContentSubText key={subTask}>{`- ${texts.eventText[subTask]}`}</ContentSubText>
                        ))}
                      </div>
                    );
                  }

                  return <ContentText key={event}>{`- ${texts.eventText[event]}`}</ContentText>;
                })}
              </ContentContainer>
              )}
            </>
          )}

          {!!camTasks.length && (
          <>
            <SubtitleText>{texts.camTasks}</SubtitleText>
            <ContentContainer>
              {camTasks.map((event) => (
                <ContentText key={event}>{`- ${texts.eventText[event]}`}</ContentText>
              ))}
            </ContentContainer>
          </>
          )}
        </SubContainer>
        {!!Object.keys(onboardingChecklistAnswers).length && (
        <SubContainer>
          <InputLabel>{texts.onboardingChecklistAnswers}</InputLabel>
          {onboardingChecklistAnswers.isAcQuestionnaire && (
            <CoachOnboardingTask
              historyItem={HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED}
              buttonText={texts.showAnswers}
              label={texts.isAcQuestionnaire}
              isCoachLeadView
              answers={onboardingChecklistAnswers.isAcQuestionnaire}
            />
          )}
          {onboardingChecklistAnswers.contentURLs && (
            <CoachOnboardingTask
              historyItem={HistoryEventType.CONTENT_URLS_SUBMITTED}
              buttonText={texts.showAnswers}
              label={texts.contentURLs}
              isCoachLeadView
              answers={onboardingChecklistAnswers.contentURLs}
            />
          )}
          {onboardingChecklistAnswers.coachOnboardingForm && (
            <CoachOnboardingTask
              historyItem={HistoryEventType.COACH_ONBOARDING_FORM_COMPLETED}
              buttonText={texts.openCoachFolder}
              label={texts.coachFolder}
              path={onboardingDoc.coachDriveFolder}
              answers={onboardingChecklistAnswers.coachOnboardingForm}
            />
          )}
        </SubContainer>
        )}
        <ConfirmDialog
          isOpen={!!completionEvent}
          onConfirm={handleConfirm}
          onCancel={() => setCompletionEvent(null)}
          dialogTexts={{
            title: texts.modalTitle,
            content: texts.eventText[completionEvent],
          }}
        />
        <LoadingOverlay isLoading={isProcessing} />
      </Container>
    </>
  );
};

PendingList.propTypes = {
  onboardingDoc: PropTypes.object.isRequired,
  history: PropTypes.array.isRequired,
  isReady: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func,
  isEnrolledInISProgram: PropTypes.bool,
};

PendingList.defaultProps = {
  onRefresh: () => {},
  isEnrolledInISProgram: false,
};

export default PendingList;
